/**
 * @file Generic frontend display formatting helpers.
 */

/**
 * Formats a number as a 5-digit string, padded with leading zeros.
 * @param {number | string | null} num - The number to format.
 * @returns {string} The zero-padded string (e.g., "00123"), or empty string if input is invalid/null.
 */
export const formatCaseFileNumber = (num) => {
    if (num === null || num === undefined || String(num).trim() === '') {return '';}
    return String(num).padStart(5, '0');
};

/**
 * Formats a numerical value as NZD currency (e.g., "$1,234.56").
 * @param {number | string | null} value - The value to format.
 * @returns {string} The formatted currency string, or an empty string if the input is invalid or null.
 */
export const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') {return '';}
    const number = Number(String(value).replace(/[$,]/g, '')); // Remove existing formatting
    if (isNaN(number)) {return '';} // Return empty if not a valid number after cleaning
    try {
        return new Intl.NumberFormat('en-NZ', {
            style: 'currency',
            currency: 'NZD',
        }).format(number);
    } catch (e) {
        console.error(`Error formatting currency for value: ${value}`, e);
        return ''; // Return empty on formatting error
    }
};

/**
 * Returns the appropriate Tailwind grid columns class based on the number of columns.
 * Designed for responsiveness (md breakpoint and up). Defaults to 4 columns for counts >= 4.
 * @param {number} count - The number of columns needed.
 * @returns {string} The Tailwind class string (e.g., "md:grid-cols-3").
 */
export function getGridColsClass(count) {
    const numCols = Math.max(1, Math.min(4, Math.floor(count || 1))); // Ensure count is 1-4
    return `md:grid-cols-${numCols}`;
}

/**
 * Checks if a hex color code represents a "dark" color, useful for determining contrasting text color (white/black).
 * Uses a standard luminance calculation.
 * @param {string | null} hexColor - The hex color code (e.g., "#FF0000" or "ff0000"). Must be 6 digits.
 * @returns {boolean} True if the color is considered dark, false otherwise or if input is invalid.
 */
export const isColorDark = (hexColor) => {
    if (!hexColor || typeof hexColor !== 'string') {return false;}
    const color = hexColor.startsWith('#') ? hexColor.substring(1) : hexColor;
    if (color.length !== 6 || /[^0-9a-fA-F]/.test(color)) {return false;} // Basic validation

    try {
        const r = parseInt(color.substring(0, 2), 16);
        const g = parseInt(color.substring(2, 4), 16);
        const b = parseInt(color.substring(4, 6), 16);
        // Standard luminance formula
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b);
        return luminance < 186; // Common threshold for distinguishing dark/light
    } catch (e) {
        console.error(`Error processing hex color: ${hexColor}`, e);
        return false; // Treat as not dark on error
    }
};
